import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ROOT_MIDI,
  getPlace,
  loadProgress,
  saveProgress,
  withEcho,
  dueEchoes,
  phraseNoteSequence,
} from "../lib/scaleCountry";
import { audioSupported, playPhraseSteps } from "../lib/scaleCountryAudio";
import ScaleKeyboard from "../components/scaleCountry/ScaleKeyboard";

/* ============================================================
   /scale-country/campfire — an ear-echo review session. Walks
   through every place whose stamp is due for an echo (computed
   once on mount) and asks you to play its saved phrase back from
   memory before refreshing the stamp.
   ============================================================ */

const ScaleCountryCampfire = () => {
  // Both the due list and the phrases they play back are fixed for the
  // whole session — re-reading progress mid-session would let an earlier
  // echo in this same session shrink the list out from under `index`.
  const [progress] = useState(loadProgress);
  const [dueIds] = useState(() => dueEchoes(loadProgress()));
  const [index, setIndex] = useState(0);
  const [matched, setMatched] = useState(0);
  const [message, setMessage] = useState("");
  const [justEchoed, setJustEchoed] = useState(false);
  const [echoedCount, setEchoedCount] = useState(0);

  const placeId = index < dueIds.length ? dueIds[index] : null;
  const place = placeId ? getPlace(placeId) : null;
  const phrase = placeId ? progress.phrases[placeId] : null;
  const target = phrase ? phraseNoteSequence(phrase) : [];
  const sessionReady = Boolean(placeId && place && target.length > 0);

  useEffect(() => {
    setMatched(0);
    setMessage("");
    setJustEchoed(false);
  }, [placeId]);

  // Stale due id (place removed, or the saved phrase went missing) — skip it
  // automatically rather than getting stuck on a place with nothing to echo.
  useEffect(() => {
    if (placeId && !sessionReady) {
      setIndex((i) => i + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, sessionReady]);

  const finished = dueIds.length > 0 && index >= dueIds.length;

  const handlePlay = () => {
    if (!phrase) return;
    setMessage("");
    playPhraseSteps(phrase, { stepMs: 280 });
  };

  const handleNote = (midi) => {
    if (justEchoed || !sessionReady) return;
    if (midi === target[matched]) {
      const next = matched + 1;
      setMessage("");
      setMatched(next);
      if (next === target.length) {
        const fresh = withEcho(loadProgress(), placeId);
        saveProgress(fresh);
        setJustEchoed(true);
        setEchoedCount((c) => c + 1);
      }
    } else {
      setMatched(0);
      setMessage("almost — listen again");
    }
  };

  const goNext = () => setIndex((i) => i + 1);

  return (
    <div className="min-h-screen bg-[#1B221A] px-4 py-10 text-[#E9EDDC] sm:px-8">
      <div className="mx-auto max-w-3xl">
        {!audioSupported() && (
          <p className="mb-4 font-mono text-xs text-[#79855F]">
            audio isn't available in this browser — the keys will be quiet
          </p>
        )}

        {dueIds.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <p className="font-mono text-sm text-[#97A18B]">
              the campfire is quiet — nothing to echo tonight
            </p>
            <Link
              to="/scale-country"
              className="font-mono text-xs text-[#A6C46A] hover:underline"
            >
              ◂ back to the map
            </Link>
          </div>
        )}

        {finished && (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <p className="font-mono text-sm text-[#97A18B]">
              campfire's out — {echoedCount} of {dueIds.length} echoed
            </p>
            <Link
              to="/scale-country"
              className="font-mono text-xs text-[#A6C46A] hover:underline"
            >
              ◂ back to the map
            </Link>
          </div>
        )}

        {!finished && dueIds.length > 0 && sessionReady && (
          <div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#79855F]">
                  CAMPFIRE · {index + 1} of {dueIds.length}
                </p>
                <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
                  {place.name}
                </h1>
              </div>
              <Link
                to="/scale-country"
                className="font-mono text-xs text-[#79855F] hover:text-[#A6C46A]"
              >
                ◂ back to the map
              </Link>
            </div>

            <div className="mt-6 rounded-lg border border-[#39432F] bg-[#222B20] p-5">
              <button
                type="button"
                onClick={handlePlay}
                className="rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A] hover:bg-[#A6C46A] hover:text-[#1B221A]"
              >
                ▶ PLAY THE PHRASE
              </button>

              <div className="mt-4 flex flex-wrap gap-1 font-mono text-lg">
                {target.map((_, i) => (
                  <span
                    key={i}
                    className={i < matched ? "text-[#A6C46A]" : "text-[#45523F]"}
                  >
                    {i < matched ? "●" : "○"}
                  </span>
                ))}
              </div>

              {message && (
                <p className="mt-2 font-mono text-xs text-[#97A18B]">{message}</p>
              )}

              {justEchoed ? (
                <div className="mt-4 flex items-center gap-3">
                  <span className="font-mono text-xs uppercase tracking-wide text-[#A6C46A]">
                    echoed ● — stamp refreshed
                  </span>
                  <button
                    type="button"
                    onClick={goNext}
                    className="rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs font-bold uppercase tracking-wide hover:border-[#A6C46A] hover:bg-[#A6C46A] hover:text-[#1B221A]"
                  >
                    {index === dueIds.length - 1 ? "DONE ▸" : "NEXT ▸"}
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex flex-wrap items-start gap-8">
                  <ScaleKeyboard
                    semis={place.semis}
                    rootMidi={ROOT_MIDI}
                    onNote={handleNote}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={goNext}
                className="mt-4 font-mono text-xs text-[#79855F] hover:text-[#E9EDDC]"
              >
                skip for tonight
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScaleCountryCampfire;
